import { Link } from "react-router-dom";
import { 
  Users, 
  Heart, 
  TrendingUp, 
  Lightbulb, 
  ShieldCheck, 
  Briefcase, 
  Mail,
  ArrowRight
} from "lucide-react";
import { COMPANY, IMG } from "@/lib/constants";
import { CTASection } from "@/components/CTASection";
import { SectionHeading, EyebrowLabel } from "@/components/SectionHeading";

const careerBenefits = [
  {
    icon: TrendingUp,
    title: "Career Growth",
    desc: "We believe in promoting from within and providing continuous learning opportunities in the electrical field."
  },
  {
    icon: Heart,
    title: "Employee Well-being",
    desc: "A supportive environment where safety, health, and work-life balance are prioritized above all."
  },
  {
    icon: Lightbulb,
    title: "Innovation First",
    desc: "Work on cutting-edge panel designs and automation solutions for high-impact industrial projects."
  },
  {
    icon: ShieldCheck,
    title: "Safety Standard",
    desc: "Strict adherence to safety protocols ensures every employee goes home safe every single day."
  }
];

export default function CareersPage() {
  return (
    <div data-testid="careers-page" className="pt-20">
      {/* HERO */}
      <section className="relative bg-navy-950 text-white overflow-hidden py-24 md:py-36">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url('${IMG.engineerOutdoor}')` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/80 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <EyebrowLabel>Join Our Team</EyebrowLabel>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[1.05] max-w-4xl">
            Powering Careers. <br />
            <span className="text-[#EAB308]">Building the Future.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-zinc-300 leading-relaxed">
            At Prime Volt Engineering Services, we don't just build panels — we build people. Join a team of dedicated professionals committed to excellence in electrical engineering and innovation.
          </p>
        </div>
      </section>

      {/* CULTURE & VALUES */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <EyebrowLabel>Our Culture</EyebrowLabel>
              <h2 className="mt-4 text-4xl sm:text-5xl font-black tracking-tighter text-navy-950 leading-tight">
                A workplace where you <br /> actually belong.
              </h2>
              <div className="mt-8 space-y-6 text-zinc-600 leading-relaxed">
                <p>
                  We believe that our greatest asset is our team. Our culture is built on mutual respect, transparent communication, and a shared passion for engineering perfection.
                </p>
                <p>
                  At Prime Volt, we care for our employees like family. From comprehensive safety training to mentorship programs, we ensure every team member has the tools and support they need to thrive.
                </p>
                <div className="pt-4 grid grid-cols-2 gap-6">
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-2 bg-[#EAB308] rounded-full" />
                    <span className="font-bold text-navy-950 uppercase tracking-wider text-xs">Collaborative</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-2 bg-[#EAB308] rounded-full" />
                    <span className="font-bold text-navy-950 uppercase tracking-wider text-xs">Safety-First</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-2 bg-[#EAB308] rounded-full" />
                    <span className="font-bold text-navy-950 uppercase tracking-wider text-xs">Innovation-Driven</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-2 bg-[#EAB308] rounded-full" />
                    <span className="font-bold text-navy-950 uppercase tracking-wider text-xs">Value-Based</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-4 -right-4 h-32 w-32 bg-[#EAB308] z-0" />
              <div className="relative z-10 bg-navy-900 p-8 md:p-12 border border-navy-800 shadow-2xl">
                <Users className="h-12 w-12 text-[#EAB308] mb-6" />
                <h3 className="text-2xl font-black text-white tracking-tight">How we care</h3>
                <p className="mt-4 text-zinc-400 text-sm leading-relaxed">
                  "The growth of the company is directly tied to the growth of our employees. We invest in people, not just machines."
                </p>
                <div className="mt-8 flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-[#EAB308] flex items-center justify-center font-bold text-navy-950">PV</div>
                  <div>
                    <div className="text-sm font-bold text-white">Management Team</div>
                    <div className="text-xs text-zinc-500">Prime Volt Engineering</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS GRID */}
      <section className="bg-zinc-50 py-24 md:py-32 border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <SectionHeading 
            eyebrow="Why Join Us?"
            title="Empowering your professional journey."
          />
          
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {careerBenefits.map((b, i) => (
              <div key={b.title} className="bg-white p-8 border border-zinc-200 hover:border-navy-950 hover:shadow-xl transition-all duration-300">
                <b.icon className="h-10 w-10 text-navy-950" strokeWidth={1.5} />
                <h3 className="mt-6 text-xl font-black tracking-tight text-navy-950 uppercase">{b.title}</h3>
                <p className="mt-4 text-sm text-zinc-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* POSITIONS STATUS */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="relative overflow-hidden bg-navy-950 rounded-[2rem] p-8 md:p-16 text-center border-4 border-navy-900 shadow-2xl">
            {/* Background elements */}
            <div className="absolute top-0 right-0 h-64 w-64 bg-navy-900 rounded-full -translate-y-1/2 translate-x-1/2 opacity-20" />
            <div className="absolute bottom-0 left-0 h-32 w-32 bg-[#EAB308] rounded-full translate-y-1/2 -translate-x-1/2 opacity-10" />
            
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center h-16 w-16 bg-[#EAB308] rounded-2xl mb-8 transform -rotate-3">
                <Briefcase className="h-8 w-8 text-navy-950" />
              </div>
              <h2 className="text-3xl md:text-5xl font-black tracking-tighter text-white uppercase">
                Positions Posting Soon
              </h2>
              <p className="mt-6 max-w-2xl mx-auto text-lg text-zinc-400 leading-relaxed">
                We are currently expanding our team and will be posting openings for Electrical Designers, Panel Technicians, and Site Engineers very soon.
              </p>
              
              <div className="mt-12 flex flex-col md:flex-row items-center justify-center gap-6">
                <div className="bg-navy-900/50 backdrop-blur-sm border border-navy-800 px-8 py-4 rounded-xl">
                  <div className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1">Status</div>
                  <div className="text-emerald-400 font-bold flex items-center gap-2">
                    <span className="h-2 w-2 bg-emerald-400 rounded-full animate-pulse" />
                    Hiring Process Starting Soon
                  </div>
                </div>
              </div>

              <div className="mt-16 pt-8 border-t border-navy-900">
                <p className="text-sm text-zinc-500 font-bold uppercase tracking-widest mb-6">Can't wait? Send your profile today.</p>
                <a 
                  href={`mailto:${COMPANY.email}?subject=Career Enquiry — [Your Name]`}
                  className="inline-flex items-center gap-3 bg-white hover:bg-[#EAB308] text-navy-950 px-8 py-4 font-black uppercase tracking-widest transition-all rounded-full"
                >
                  <Mail className="h-5 w-5" />
                  Drop Your Resume
                  <ArrowRight className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
